import { ErrorMessage, FieldArray, Formik, FormikErrors } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { FormNavButtons } from '../../../../components/Additional/FromButtons';
import { VerticalForm } from '../../../../components/Additional/VerticalForm';
import { FormContext } from '../../../../components/Additional/FormWrapper';
import { Label } from '@radix-ui/react-label';
import { Textarea } from '../../../../components/UiComponents/textarea';
import { validator } from '../../../../components/Additional/validator';
import { FormType } from '../../../../components/Additional/FormType';
import CustomSelect from '../../../../components/UiComponents/selectFieldGroup';
import { AuthContext } from '../../../../components/AuthWrapper';
import { useOutletContext } from 'react-router-dom';
import SelectComponentModal from '../../../../components/SelectItemComponent';
import { Button } from '../../../../components/UiComponents/button';
import toast from 'react-hot-toast';
import { experienceTypeOptions, InterestsAndActivitiesInitialData, loadFromLocalStorageInterests, saveToLocalStorage, takeStudentDataFromContextInterests, yesNoMaybeDonNotKnowOptions, yesNoMaybeOptions } from '../Helpers';
import { localStorageKeyInterests } from '../../../../components/LocalStorageProcesser';
import { FieldWrapper } from '../../../../components/Additional/FieldWrapper';
import { FormHeader } from '../FormHeader';
import { Input } from '../../../../components/UiComponents/input';
import { PlusCircle, XLg } from 'react-bootstrap-icons';
import { Experience, ExperienceType } from '../../../../api/client';

export function InterestsActivitiesGoals(): JSX.Element {
  // Takes data of the part of the form to entire form
  const { formData, onSubmit, isCreating } = useContext(FormContext);

  // Static data loaded into context like interests
  const { staticData }: AuthContext = useOutletContext();

  // Controls the model that selects professional (academic) and personal interests
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // Stores the list of all available professional and personal interests
  const [allProfessionalInterests, setAllProfessionalInterests] = useState<string[]>([]);
  const [allPersonalInterests, setAllPersonalInterests] = useState<string[]>([]);
  const [allActivities, setAllActivities] = useState<string[]>([]);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);

  // Updates the list of all avaliable professional and personal interests
  useEffect(() => {
    setAllProfessionalInterests(staticData?.academicInterests?.map(interest => interest.name) ?? []);
    setAllPersonalInterests(staticData?.personalInterests?.map(interest => interest.name) ?? []);
    setAllActivities(staticData?.activities?.map(activity => activity.name) ?? []);
    setAllLanguages(staticData?.languages?.map(language => language.name) ?? []);
  }, [staticData]);

  const dataFromLocalStorage: InterestsAndActivitiesInitialData = isCreating ? loadFromLocalStorageInterests() : takeStudentDataFromContextInterests(formData);
  const [formValues] = useState<InterestsAndActivitiesInitialData>(dataFromLocalStorage);

  // Stores list of selected professional and personal interestss
  const [selectedProfessionalInterests, setSelectedProfessionalInterests] = useState<string[]>(formValues.academicInterestsList ?? []);
  const [selectedPersonalInterests, setSelectedPersonalInterests] = useState<string[]>(formValues.personalInterestsList ?? []);
  const [selectedActivities, setSelectedActivities] = useState<string[]>(formValues.activityList ?? []);
  const [selectedSpokenLanguagesList, setSelectedSpokenLanguagesList] = useState<string[]>(formValues.languagesSpokenList ?? []);

  // Closes and opens modal for selecting interests
  const handleOpenModal = (field: string) => {
    setSelectedField(field);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedField(null);
  };

  // Takes data for the model of all interests
  const getModalItems = (field: string): string[] => {
    switch (field) {
      case 'personalInterest':
        return allPersonalInterests;
      case 'professionalInterest':
        return allProfessionalInterests;
      case 'activity':
        return allActivities;
      case 'languagesSpokenList':
        return allLanguages;
      default:
        return [];
    }
  };

  // Gets data from the model of selected interests
  const getSelectedItems = (field: string): string[] => {
    switch (field) {
      case 'personalInterest':
        return selectedPersonalInterests;
      case 'professionalInterest':
        return selectedProfessionalInterests;
      case 'activity':
        return selectedActivities;
      case 'languagesSpokenList':
        return selectedSpokenLanguagesList;
      default:
        return [];
    }
  };

  // Saves selected items into the formik
  const handleSaveItems = (items: string[], setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<any>>) => {
    if (!selectedField) return;
    switch (selectedField) {
      case 'personalInterest':
        setFieldValue('personalInterestsList', items);
        toast.success('Personal interestes saved');
        break;
      case 'professionalInterest':
        setFieldValue('academicInterestsList', items);
        toast.success('Academic/professional interestes saved');
        break;
      case 'activity':
        setFieldValue('activityList', items);
        toast.success('Activities saved');
        break;
      case 'languagesSpokenList':
        setFieldValue('languagesSpokenList', items);
        toast.success('Languages you speak saved');
        break;
    }
    handleCloseModal();
  };

  // Selected select field (dropdown) data
  const [selectedGraduateSchoolValue, setSelectedGraduateSchoolValue] = useState<string>(formValues.gradSchoolPlans);
  const [selectedStudyAbroadValue, setSelectedStudyAbroadValue] = useState<string>(formValues.gradProgramsAbroadInterest);
  const [selectedKnowsHipsValue, setSelectedKnowsHipsValue] = useState<string>(formValues.knowsHipsValue);

  return (
    <Formik
      initialValues={formValues}
      enableReinitialize
      onSubmit={(values) => {
        onSubmit(values);
      }}
      validate={validator(FormType.InterestsAcitivitesGoals)}
    >
      {({ values, handleChange, setFieldValue, setValues, errors }) => {
        useEffect(() => {
          console.log('errors', errors);
        }, [errors]);
        useEffect(() => {
          if (isCreating) {
            saveToLocalStorage(localStorageKeyInterests, values);
          }
        }, [values]);

        // Sets data from dropdowns (select fields) into Formik
        useEffect(() => {
          // Set all values with a single setValues call
          setValues((prevValues: InterestsAndActivitiesInitialData) => ({
            ...prevValues,
            'gradSchoolPlans': selectedGraduateSchoolValue,
            'gradProgramsAbroadInterest': selectedStudyAbroadValue,
            'knowsHipsValue': selectedKnowsHipsValue
          }));
        }, [selectedGraduateSchoolValue, selectedStudyAbroadValue, selectedKnowsHipsValue]);

        return (
          <>
            <VerticalForm>
              <FormHeader stepTitle="Interests, Activities and Goals (2/2)" />
              <div className="space-y-4">
                <div>
                  <Label className="block text-sm font-medium mb-3">
                    1. Please select tags to describe your academic and professional interests (max of 10) <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <FieldWrapper name={"academicInterestsList"}>
                    <Button type="button" onClick={() => handleOpenModal('professionalInterest')} abbr="Click to select interests">
                      Select Interests
                    </Button>
                  </FieldWrapper>
                  <div className='mt-2'>Selected {selectedProfessionalInterests.length} {selectedProfessionalInterests.length == 1 ? 'interest' : 'interests'}.</div>
                </div>
                <div>
                  <Label className="block text-sm font-medium mb-3">2. Please select tags to describe your personal interests (no maximum) <span className="text-red-500 ml-1">*</span></Label>
                  <FieldWrapper name={"personalInterestsList"}>
                    <Button type="button" onClick={() => handleOpenModal('personalInterest')} abbr="Click to select interests">
                      Select Interests
                    </Button>
                  </FieldWrapper>
                  <div className='mt-2'>Selected {selectedPersonalInterests.length} {selectedPersonalInterests.length == 1 ? 'interest' : 'interests'}.</div>
                </div>
                <div>
                  <Label className="block text-sm font-medium mb-3">3. In high school, did you participate in any of the following?<span className="text-red-500 ml-1">*</span></Label>
                  <FieldWrapper name={"activityList"}>
                    <Button type="button" onClick={() => handleOpenModal('activity')} abbr="Click to select activities">
                      Select Activities
                    </Button>
                  </FieldWrapper>
                  <div className='mt-2'>Selected {selectedActivities.length} {selectedActivities.length == 1 ? 'activity' : 'activities'}.</div>
                </div>
                <CustomSelect
                  label="4. Do you plan to attend graduate school?"
                  options={yesNoMaybeOptions}
                  value={selectedGraduateSchoolValue}
                  onChange={setSelectedGraduateSchoolValue}
                  required
                  name={"gradSchoolPlans"}
                />
                <CustomSelect
                  label="5. Are you interested in graduate programs, research, or teaching abroad?"
                  options={yesNoMaybeOptions}
                  value={selectedStudyAbroadValue}
                  onChange={setSelectedStudyAbroadValue}
                  required
                  name="gradProgramsAbroadInterest"
                />
                <div>
                  <Label className="block text-sm font-medium mb-3">6. Do you speak any languages other than English?</Label>
                  <FieldWrapper name={"languagesSpoken"}>
                    <Button type="button" onClick={() => handleOpenModal('languagesSpokenList')} abbr="Click to select languages">
                      Select Languages
                    </Button>
                  </FieldWrapper>
                  <div className='mt-2'>Selected {selectedSpokenLanguagesList.length} {selectedSpokenLanguagesList.length == 1 ? 'language' : 'languages'}.</div>
                </div>
                <Textarea
                  name="futureGoals"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  value={values.futureGoals}
                  onChange={handleChange}
                  title="7. Please describe your future academic and professional goals as you envision them right now:"
                  required
                />
                <Textarea
                  name="passionProjects"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  value={values.passionProjects}
                  onChange={handleChange}
                  title="8. Do you have passion projects you wish you could give more time to, but you don't because you think your major and studies conflict with pursuing them?"
                />
                <CustomSelect
                  label="9. Have you learned about the value of “high impact practices” at USF?"
                  options={yesNoMaybeDonNotKnowOptions}
                  value={selectedKnowsHipsValue}
                  onChange={setSelectedKnowsHipsValue}
                  required
                  name="knowsHipsValue"
                />
                <div className="block text-sm font-medium ">
                  10. Add Experiences
                </div>
                <div>
                  Add details of past or current experiences—such as jobs, internships, research, publications, or conference presentations—to your ONS profile and keep updating it as you get more involved. These are not limited to on-campus activities. This helps us connect you with the right resources and celebrate your accomplishments. 
                </div>
                <FieldArray name="experienceList">
                {({ remove, push }) => (
                  <div className="w-full">
                    {/* Experiences Input */}
                    <div className="grid w-full grid-cols-1 sm:grid-cols-3 grid-rows-1 gap-x-5 gap-y-5">
                      {values.experienceList.map((elem, index) => (
                        <div key={index} className="grid grid-cols-1 p-3 border-2 dark:border-slate-400 rounded-xl">
                          <Button
                            onClick={() => remove(index)}
                            className="rounded-full max-w-[2rem] max-h-[2rem] mb-2"
                            abbr="Click to remove experience"
                          >
                            <div className="inline-flex items-center justify-center space-x-2">
                              <XLg className="w-4 h-4" />
                            </div>
                          </Button>
                          <Input
                            type="text"
                            name={`experienceList.${index}.title`}
                            value={elem.title}
                            onChange={handleChange}
                            className="mb-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            title={"Title"}
                            required
                          />
                          <ErrorMessage
                            name={`experienceList.${index}.title`}
                            component="div"
                            className="text-red-500 text-sm mt-1 mb-3"
                          />
                          <Input
                            type="text"
                            name={`experienceList.${index}.role`}
                            value={elem.role}
                            onChange={handleChange}
                            className="mb-3 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            title={"Role"}
                            required
                          />
                          <ErrorMessage
                            name={`experienceList.${index}.role`}
                            component="div"
                            className="text-red-500 text-sm mt-1 mb-3"
                          />
                          <Input
                            type="number"
                            min={0} // Min of 0 years (maybe they are in their first year)
                            name={`experienceList.${index}.yearsNumber`}
                            value={elem.yearsNumber}
                            onChange={handleChange}
                            className="mb-3 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            title={"Number of Years"}
                            required
                          />
                          <ErrorMessage
                            name={`experienceList.${index}.yearsNumber`}
                            component="div"
                            className="text-red-500 text-sm mt-1 mb-3"
                          />
                          <CustomSelect
                            label="Type of Activity"
                            options={experienceTypeOptions}
                            value={String(elem.type)}
                            onChange={(val) =>
                              setFieldValue(`experienceList.${index}.type`, val)
                            }                            
                            required
                            name={`experienceList.${index}.type`}
                          />
                          <Input
                            type="text"
                            name={`experienceList.${index}.mentor`}
                            value={elem.mentor}
                            onChange={handleChange}
                            className="mb-3 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            title={"Mentor"}
                          />
                          <Textarea
                            name={`experienceList.${index}.description`}
                            className="mb-3 block w-full border-gray-300 rounded-md shadow-sm"
                            value={elem.description}
                            onChange={handleChange}
                            title="Description"
                          />
                        </div>
                      ))
                      }
                      {/* Add Button (capped at 15 links) */}
                      {
                        values.experienceList.length < 15 && (
                          <Button
                            type="button"
                            onClick={() =>
                              push(
                                new Experience({
                                  title: "",
                                  role: "",
                                  mentor: "",
                                  description: "",
                                  yearsNumber: 0,
                                  type: ExperienceType.Research
                                })
                              )
                            }
                            className="gap-2"
                            abbr="Click to add experience"
                          >
                            <PlusCircle className="w-5 h-5" />
                            <span className="mr-2">Add Experience</span>
                          </Button>
                        )
                      }
                    </div >
                  </div >
                )}
              </FieldArray >
              </div>
              <FormNavButtons />
            </VerticalForm>
            <SelectComponentModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              onSave={(items) => handleSaveItems(items, setFieldValue)}
              allItems={selectedField ? getModalItems(selectedField) : []}
              selectedItems={selectedField ? getSelectedItems(selectedField) : []}
              setSelectedItems={(items) => {
                if (!selectedField) return;
                switch (selectedField) {
                  case 'personalInterest':
                    setSelectedPersonalInterests(items);
                    break;
                  case 'professionalInterest':
                    setSelectedProfessionalInterests(items);
                    break;
                  case 'activity':
                    setSelectedActivities(items);
                    break;
                  case 'languagesSpokenList':
                    setSelectedSpokenLanguagesList(items);
                    break;
                }
              }}
            />
          </>);
      }}
    </Formik>
  );
}
